/**
 * Oversikt (manager redesign, 2026-10-09): Dagen, the day as a list, and Oppfølging, what needs a
 * decision across dates. Both tabs read the same data, so the Oppfølging badge counts the same
 * points the queue shows. Addresses: /hjem and /dagen open Dagen (?d= a date, ?lag= a team,
 * ?vis= an attendance filter, ?valgt= a shift's details), /varsler opens Oppfølging.
 */
import { useShifts } from '@/api/data';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { today as osloToday, tenantRules } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Skeleton } from '@/ui';
import { Icon } from '@/ui/icons';
import { PageHead, TabPanel, Tabs } from '@/ui/page';
import { addDays } from '@timefly/core';
import { Link, useSearchParams } from 'react-router-dom';
import { useNow } from '../min/parts';
import { ProjectNotes } from '../prosjekter/Budget';
import { toVaktplan } from '../vaktplan/link';
import { DagenTab } from './DagenTab';
import { OppfolgingTab } from './OppfolgingTab';
import { dayRows, shiftsOfDay } from './day';
import { overviewLinks } from './navigation';
import { useFollowUpQueue } from './useQueue';
import './oversikt.css';

export type OversiktTab = 'dagen' | 'oppfolging';

export default function Oversikt({ tab, base }: { tab: OversiktTab; base?: string }) {
  const can = useCan();
  const { tenant, mode, workspace } = useTenant();
  const [sp] = useSearchParams();
  const now = useNow(60_000).getTime();
  const today = osloToday();
  const d = /^\d{4}-\d{2}-\d{2}$/.test(sp.get('d') ?? '') ? (sp.get('d') as string) : today;
  const rota = mode === 'rota' && workspace.enabled.scheduling;
  const seesTime = can('timeliste.se');

  // the day's list: from the day before, for a night shift or an open punch carried over
  const dayQ = useShifts(addDays(d, -1), d);

  const tol = tenantRules().toleranceMin ?? 15;
  const dayShifts = shiftsOfDay(dayQ.data?.shifts ?? [], d);
  const rows = dayRows(dayShifts, now, tol, rota);
  // the queue's attendance points are always today's: this list's rows when it shows today
  const fu = useFollowUpQueue(today, now);
  const queue = fu.queue;
  const queueUnknown = fu.loading || fu.failed;

  if (!tenant) return <Skeleton />;

  const links = overviewLinks(tab, sp, base === '/dagen' ? '/dagen' : '/hjem');
  return (
    <div className="ox">
      <PageHead
        title={T('Oversikt')}
        sub={tenant.name}
        actions={
          <>
            {can('assistent.bruke') && (
              <button
                type="button"
                className="btn oxhugin"
                onClick={() => window.dispatchEvent(new Event('timefly:ai-open'))}
                aria-label={T('Spør Hugin')}
              >
                <Icon name="sparkle" />
                {T('Hugin')}
                <span className="oxhugin-unread" aria-label={T('Ny melding')}>
                  1
                </span>
              </button>
            )}
            {rota && can('vaktplan.se') ? (
              <Link className="btn" to={toVaktplan({ day: d })} aria-label={T('Åpne dagsplan')}>
                <Icon name="calendar" />
                <span className="oxplan-full">{T('Åpne dagsplan')}</span>
                <span className="oxplan-short">{T('Dagsplan')}</span>
              </Link>
            ) : null}
          </>
        }
      >
        <Tabs
          label={T('Oversikt')}
          value={tab}
          items={[
            { key: 'dagen', label: T('Dagen'), to: links.day },
            ...(seesTime
              ? [
                  {
                    key: 'oppfolging',
                    label: T('Oppfølging'),
                    to: links.queue,
                    count: queueUnknown ? null : queue.points.length,
                    aria: queueUnknown
                      ? T('Oppfølging')
                      : T('Oppfølging, {n} punkter', { n: queue.points.length }),
                  },
                ]
              : []),
          ]}
        />
      </PageHead>
      {/* Hugin's word on the project budgets and the month close (#132), where projects are on */}
      {tab === 'dagen' && seesTime && <ProjectNotes />}
      <TabPanel tab={tab}>
        {tab === 'dagen' ? (
          <DagenTab
            d={d}
            today={today}
            now={now}
            rows={rows}
            rota={rota}
            shiftsQ={dayQ}
            dayShifts={dayShifts}
          />
        ) : (
          <OppfolgingTab
            queue={queue}
            loading={fu.loading}
            failed={fu.failed}
            unsure={fu.unsure}
            onRetry={fu.refetch}
          />
        )}
      </TabPanel>
    </div>
  );
}
